import React, { useEffect, useState } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { CSpinner } from '@coreui/react-pro'

import { api, ApiError, type AuthUser } from './api/client'
import Layout from './Layout'
import Activate from './pages/Activate'
import NeedLink from './pages/NeedLink'
import Dashboard from './pages/Dashboard'
import Calendar from './pages/Calendar'
import People from './pages/People'
import Flights from './pages/Flights'
import Stays from './pages/Stays'
import Transport from './pages/Transport'
import Activities from './pages/Activities'
import Ideas from './pages/Ideas'
import IdeaDiscussion from './pages/IdeaDiscussion'
import Money from './pages/Money'
import Tasks from './pages/Tasks'
import Ask from './pages/Ask'
import Admin from './pages/Admin'

interface AuthState {
  user: AuthUser | null
  bootstrapped: boolean
}

export const AuthContext = React.createContext<{
  auth: AuthState
  refresh: () => Promise<void>
  setUser: (user: AuthUser) => void
  clear: () => void
}>({
  auth: { user: null, bootstrapped: false },
  refresh: () => Promise.resolve(),
  setUser: () => {},
  clear: () => {},
})

const App: React.FC = () => {
  const [auth, setAuth] = useState<AuthState>({ user: null, bootstrapped: false })

  const refresh = () => {
    setAuth({ user: null, bootstrapped: false })
    return api
      .get<AuthUser>('/v1/auth/me')
      .then((user) => setAuth({ user, bootstrapped: true }))
      .catch((_err: ApiError) => setAuth({ user: null, bootstrapped: true }))
  }

  const setUser = (user: AuthUser) => setAuth({ user, bootstrapped: true })

  useEffect(() => {
    refresh()
  }, [])

  useEffect(() => {
    const onExpired = () => setAuth({ user: null, bootstrapped: true })
    window.addEventListener('auth-expired', onExpired)
    return () => window.removeEventListener('auth-expired', onExpired)
  }, [])

  const clear = () => setAuth({ user: null, bootstrapped: true })

  return (
    <AuthContext.Provider value={{ auth, refresh, setUser, clear }}>
      <Routes>
        <Route path="/auth/activate" element={<Activate />} />
        <Route path="/login" element={<NeedLink />} />
        <Route
          path="/*"
          element={
            <RequireAuth>
              <Layout>
                <Routes>
                  <Route path="/" element={<Dashboard />} />
                  <Route path="/calendar" element={<Calendar />} />
                  <Route path="/people" element={<People />} />
                  <Route path="/flights" element={<Flights />} />
                  <Route path="/stays" element={<Stays />} />
                  <Route path="/transport" element={<Transport />} />
                  <Route path="/activities" element={<Activities />} />
                  <Route path="/ideas" element={<Ideas />} />
                  <Route path="/ideas/:id/discussion" element={<IdeaDiscussion />} />
                  <Route path="/tasks" element={<Tasks />} />
                  <Route path="/ask" element={<Ask />} />
                  <Route path="/money" element={<Money />} />
                  <Route path="/admin" element={<Admin />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </Layout>
            </RequireAuth>
          }
        />
      </Routes>
    </AuthContext.Provider>
  )
}

const RequireAuth: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { auth } = React.useContext(AuthContext)
  const location = useLocation()
  if (!auth.bootstrapped) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <CSpinner color="primary" />
      </div>
    )
  }
  if (!auth.user) {
    const next = encodeURIComponent(location.pathname + location.search)
    return <Navigate to={`/login?next=${next}&expired=1`} replace />
  }
  return <>{children}</>
}

export default App
